import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { AttendanceRecordSource } from '@schooly/analytics';
import { AttendanceCode, AttendanceSessionEnum, JustificationBase } from '@schooly/api';
import { AttendanceCodeCircle } from '@schooly/components/attendance';
import { useAuth } from '@schooly/components/authentication';
import {
  DropdownIcon,
  ListAccordion,
  ListAccordionContext,
  Skeleton,
  TruncatedLinks,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { FC, ReactNode, useContext, useMemo } from 'react';
import { useFormContext } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { allowRenderGroups } from '../../../../helpers/renderRoutesByPermissions';
import { ViewGroupModalTabs } from '../../../Groups/utils';
import { AttendanceGridStudent } from '../common/types';
import { AttendanceSidebarEntryContent } from './AttendanceSidebarEntryContent';
import {
  AttendanceFormEntries,
  getEntryGroupKey,
  getEntryGroups,
  getFormEntryIndex,
  SidebarEntry,
  AttendancePeriod,
} from './utils';

const COLLAPSED_CIRCLE_SIZE = 28;

type AttendanceSidebarEntryProps = {
  entry: SidebarEntry;
  student: AttendanceGridStudent;
  justification: JustificationBase | null;
  codesById: Record<string, AttendanceCode>;
  date: string;
  source: AttendanceRecordSource;
  collapsible: boolean;
  disabled?: boolean;
  defaultExpanded?: boolean;
};

export const AttendanceSidebarEntry: FC<AttendanceSidebarEntryProps> = ({
  entry,
  student,
  justification,
  codesById,
  date,
  source,
  collapsible,
  disabled,
  defaultExpanded,
}) => {
  const header = <EntryHeader entry={entry} codesById={codesById} collapsible={collapsible} />;
  const content = (
    <AttendanceSidebarEntryContent
      entry={entry}
      student={student}
      justification={justification}
      codesById={codesById}
      date={date}
      source={source}
      disabled={disabled}
    />
  );

  if (!collapsible) {
    return (
      <Stack gap={2}>
        {header}
        {content}
      </Stack>
    );
  }

  return (
    <ListAccordion
      defaultExpanded={defaultExpanded}
      header={header}
      sx={{ border: 'none', borderRadius: 0, overflow: 'visible' }}
    >
      <Box pt={2}>{content}</Box>
    </ListAccordion>
  );
};

export const AttendanceSidebarEntrySkeleton = () => {
  return (
    <Stack gap={2}>
      <Skeleton height={28} width={120} />
      <Skeleton height={42} width="100%" />
      <Stack gap={0.5}>
        <Skeleton height={18} width={60} />
        <Skeleton height={22} width={100} />
      </Stack>
      <Skeleton height={20} width={60} />
    </Stack>
  );
};

type EntryHeaderProps = Pick<AttendanceSidebarEntryProps, 'entry' | 'codesById' | 'collapsible'>;

const EntryHeader: FC<EntryHeaderProps> = ({ entry, codesById, collapsible }) => {
  const { formatMessage } = useIntl();
  const { permissions } = useAuth();
  const canViewGroups = allowRenderGroups(permissions);
  const accordion = useContext(ListAccordionContext);
  const { watch } = useFormContext<AttendanceFormEntries>();

  const formEntries = watch('entries');
  const attendanceCodeId = formEntries?.[getFormEntryIndex(formEntries, entry)]?.attendance_code_id;

  const codeAvatar = useMemo((): ReactNode => {
    const code = attendanceCodeId ? codesById[attendanceCodeId] : undefined;
    return <AttendanceCodeCircle code={code} size={COLLAPSED_CIRCLE_SIZE} variant="h3" />;
  }, [attendanceCodeId, codesById]);

  const groupLinks = useMemo(
    () =>
      getEntryGroups(entry).map((group) => {
        const { id, name, separator } = group;

        return {
          key: getEntryGroupKey(group),
          name: `${name}${separator}`,
          groupName: name,
          link: { to: `/groups/${id}?tab=${ViewGroupModalTabs.Attendance}`, target: '_blank' },
        };
      }),
    [entry],
  );

  const showCollapsedCodeAvatar = collapsible && !accordion.expanded;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={1}
      width="100%"
      onClick={collapsible ? accordion.toggle : undefined}
      sx={{ cursor: collapsible ? 'pointer' : undefined }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        px={0.5}
        minWidth={0}
        width="fit-content"
        maxWidth="100%"
        sx={(theme) => ({ bgcolor: 'background.default', borderRadius: theme.spacing(0.5) })}
      >
        <Typography variant="h2" component="span" noWrap>
          {entry.session &&
            formatMessage({
              id: entry.session === AttendanceSessionEnum.Am ? 'attendance-AM' : 'attendance-PM',
            })}
          {entry.periods.map((period, index) => (
            <Box key={period.lesson.id} component="span">
              {(entry.session || index > 0) && '/'}
              <EntryHeaderPeriod period={period} />
            </Box>
          ))}
        </Typography>

        {groupLinks.length > 0 && (
          <>
            <Typography variant="h2">•</Typography>
            <Stack minWidth={0} flex={1} onClick={(e) => e.stopPropagation()}>
              {canViewGroups ? (
                <TruncatedLinks
                  items={groupLinks}
                  separator=""
                  typographyProps={{ variant: 'h2', sx: { '& a': { color: 'primary.main' } } }}
                  tooltipProps={{
                    componentsProps: { tooltip: { sx: { width: 360, maxWidth: 360, p: 1.25 } } },
                    title: (
                      <Stack alignItems="flex-start">
                        {groupLinks.map(({ key, groupName, link }) => (
                          <Typography
                            key={key}
                            variant="h3"
                            component={Link}
                            to={link.to}
                            target={link.target}
                            sx={{
                              '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                            }}
                          >
                            {groupName}
                          </Typography>
                        ))}
                      </Stack>
                    ),
                  }}
                />
              ) : (
                <TypographyWithOverflowHint variant="h2" noWrap>
                  {groupLinks.map(({ name }) => name).join('')}
                </TypographyWithOverflowHint>
              )}
            </Stack>
          </>
        )}
      </Stack>

      {collapsible && (
        <Stack direction="row" alignItems="center" gap={1}>
          {showCollapsedCodeAvatar && codeAvatar}
          <IconButton
            inverse={!accordion.expanded}
            sx={{ transform: accordion.expanded ? 'rotate(180deg)' : undefined }}
          >
            <DropdownIcon />
          </IconButton>
        </Stack>
      )}
    </Stack>
  );
};

const EntryHeaderPeriod: FC<{ period: AttendancePeriod }> = ({ period }) => (
  <Tooltip
    arrow={false}
    placement="bottom"
    title={period.lesson.name}
    slotProps={{
      popper: {
        sx: (theme) => ({
          '&.MuiTooltip-popper[data-popper-placement*="bottom"] .MuiTooltip-tooltip': {
            marginTop: theme.spacing(0.25),
          },
        }),
      },
    }}
    componentsProps={{
      tooltip: {
        sx: (theme) => ({
          p: theme.spacing(0.25, 0.5),
          typography: 'caption',
          color: 'common.grey2',
        }),
      },
    }}
  >
    <Box component="span">{period.lesson.order}</Box>
  </Tooltip>
);
